// src/pages/cadastros/Status/StatusPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Can } from "../../../components/Can";

import { useTheme } from "../../../contexts/ThemeContext";
import { useAuth } from "../../../contexts/AuthContext";
import { useEmpresa } from "../../../contexts/EmpresaContext";
import { useIsMobile } from "../../../hooks/useIsMobile";
import LayoutDesktop from "../../../layouts/LayoutDesktop";
import LayoutMobile from "../../../layouts/LayoutMobile";
import FiltroBar from "../../../components/FiltroBar/FiltroBar";
import { filtrarPorEmpresa } from "../../../utils/empresaScope";
import CardListMobile from "../../../templates/mobile/CardListMobile";

import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
} from "firebase/firestore";

function renderCodigoStatus(s) {
  if (s.codigo) return s.codigo;
  if (s.codigoSequencial != null) {
    return `ST${String(s.codigoSequencial).padStart(4, "0")}`;
  }
  if (s.id) return s.id.slice(0, 8);
  return "-";
}

function ehStatusApp(s) {
  return !!s.padrao;
}

function StatusPage() {
  const { modoGlobal, empresaAtual } = useEmpresa();
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile(900);

  const [statusList, setStatusList] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);

  const [campoFiltro, setCampoFiltro] = useState("nome");
  const [textoFiltro, setTextoFiltro] = useState("");

  const camposFiltro = [
    { value: "codigo", label: "Código" },
    { value: "nome", label: "Nome" },
    { value: "empresaNome", label: "Empresa" },
    { value: "observacoes", label: "Observações" },
  ];

  useEffect(() => {
    async function carregarStatus() {
      if (!usuarioLogado) return;

      setCarregando(true);
      setErro("");

      try {
        const statusRef = collection(db, "statusOs");
        let q;

        if (usuarioLogado.grupoId) {
          q = query(statusRef, where("grupoId", "==", usuarioLogado.grupoId));
        } else {
          q = query(statusRef, where("empresaNome", "==", "__nulo__"));
        }

        const snap = await getDocs(q);
        const lista = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));

        
        // adicionar status de fábrica (padrão do sistema)
        const fabrica = [
          {id:"fab_aberta", nome:"Aberta", empresaNome:"-", cor:"#2563eb", observacoes:"Status inicial padrão", padrao:true},
          {id:"fab_aberta_24h", nome:"Aberta 24H", empresaNome:"-", cor:"#7c3aed", observacoes:"OS urgente - atendimento em até 24 horas", padrao:true},
          {id:"fab_em_execucao", nome:"Em Execução", empresaNome:"-", cor:"#0891b2", observacoes:"OS em execução pelo técnico", padrao:true},
          {id:"fab_finalizada", nome:"Finalizada", empresaNome:"-", cor:"#16a34a", observacoes:"OS concluída", padrao:true},
          {id:"fab_cancelada", nome:"Cancelada", empresaNome:"-", cor:"#dc2626", observacoes:"OS cancelada", padrao:true},
          {id:"fab_aguard_mat", nome:"Aguardando Material", empresaNome:"-", cor:"#ca8a04", observacoes:"Aguardando chegada de material", padrao:true},
          {id:"fab_solic_mat", nome:"Solicitação de Material", empresaNome:"-", cor:"#d97706", observacoes:"Material foi solicitado", padrao:true},
          {id:"fab_aguard_parada", nome:"Aguardando Parada de Máquina", empresaNome:"-", cor:"#64748b", observacoes:"Aguardando parada programada", padrao:true}
        ];
        const listaComFabrica = [...fabrica, ...lista];
        setStatusList(listaComFabrica);

      } catch (e) {
        console.error("Erro ao carregar status:", e);
        setErro("Erro ao carregar status. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarStatus();
  }, [usuarioLogado]);

  const statusFiltrados = statusList.filter((s) => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return true;

    let valorBase = "";

    if (campoFiltro === "codigo") {
      valorBase = renderCodigoStatus(s);
    } else {
      valorBase = s[campoFiltro] ?? "";
    }

    return String(valorBase).toLowerCase().includes(texto);
  });

  async function handleExcluir(statusId) {
    if (!statusId) return;

    const statusSelecionado = statusList.find((s) => s.id === statusId);
    if (statusSelecionado?.padrao) {
      alert("Status padrão do sistema não pode ser excluído.");
      return;
    }

    const confirmar = window.confirm(
      "Tem certeza que deseja excluir este status? Essa ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setCarregando(true);
    setErro("");

    try {
      await deleteDoc(doc(db, "statusOs", statusId));
      setStatusList((prev) => prev.filter((s) => s.id !== statusId));
    } catch (e) {
      console.error("Erro ao excluir status:", e);
      setErro("Erro ao excluir status. Tente novamente.");
    } finally {
      setCarregando(false);
    }
  }

  const itemsParaCards = statusFiltrados.map((s) => ({
    id: s.id,
    nome: s.nome || "Sem nome",
    subtitulo: s.empresaNome || "-",
    detalhes: [
      { label: "Código", valor: renderCodigoStatus(s) },
      {
        label: "Cor",
        valor: s.cor || "-",
      },
      { label: "Observações", valor: s.observacoes || "-" },
      ...(ehStatusApp(s)
        ? [{ label: "Status do APP", valor: "Não pode ser alterado" }]
        : []),
    ],
    corBadge: s.cor || undefined,
  }));

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Status"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
        abaBottom="cadastros"
      >
        <CardListMobile
          items={itemsParaCards}
          carregando={carregando}
          erro={erro}
          carregandoMessage="Carregando status..."
          titulo="Status de OS"
          searchPlaceholder="Buscar status..."
          novoLabel="Novo Status"
          emptyMessage="Nenhum status encontrado"
          onHome={() => navigate("/")}
          onNovo={() => navigate("/cadastros/status/novo")}
          onEditar={(id) => navigate(`/cadastros/status/editar/${id}`)}
          getSearchText={(item) => `${item.nome} ${item.subtitulo}`}
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Novo Status"
      subtitulo=""
    >
      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Status de Ordens de Serviço</h3>
            <p>
              Lista de status de OS por empresa do grupo atual. Alguns status
              são padrão do sistema e não podem ser alterados.
            </p>
          </div>

          <div className="pc-table-header-actions">
            <Can modulo="cadastros" recurso="status" acao="form">
<button
              type="button"
              className="pc-btn-primario"
              onClick={() => navigate("/cadastros/status/novo")}
            >
              + Novo Status
            </button>
</Can>
          </div>
        </header>

        <FiltroBar
          campos={camposFiltro}
          campoSelecionado={campoFiltro}
          onCampoChange={setCampoFiltro}
          textoFiltro={textoFiltro}
          onTextoChange={setTextoFiltro}
          onAdicionarClick={() => navigate("/cadastros/status/novo")}
          placeholder="Filtrar status..."
          mostrarAdicionar={false}
        />

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando status...</p>
          ) : statusFiltrados.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhum status encontrado.</p>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Empresa</th>
                  <th>Cor</th>
                  <th>Observações</th>
                  <th>Status do APP</th>
                  <th style={{ textAlign: "right" }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {statusFiltrados.map((s) => (
                  <tr key={s.id}>
                    <td>{renderCodigoStatus(s)}</td>
                    <td>{s.nome || "-"}</td>
                    <td>{s.empresaNome || "-"}</td>
                    <td>
                      {s.cor ? (
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <span
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: 999,
                              border: "1px solid #020617",
                              backgroundColor: s.cor,
                              display: "inline-block",
                            }}
                          />
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: 12,
                            }}
                          >
                            {s.cor}
                          </span>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>{s.observacoes || "-"}</td>
                    <td>
                    {ehStatusApp(s) ? (
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 999,
                          fontSize: 12,
                          background: "#0369a1",
                          color: "#e5e7eb",
                          border: "1px solid rgba(15, 23, 42, 0.4)",
                          letterSpacing: 0.3,
                        }}
                      >
                        Status do APP
                      </span>
                    ) : (
                      "-"
                    )}
                  </td>
                    <td
                      style={{
                        textAlign: "right",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <button
                        type="button"
                        title={
                          s.padrao
                            ? "Status padrão não pode ser editado"
                            : "Editar status"
                        }
                        onClick={() =>
                          !s.padrao &&
                          navigate(`/cadastros/status/editar/${s.id}`)
                        }
                        style={{
                          border: "none",
                          
                          cursor: s.padrao ? "not-allowed" : "pointer",
                          fontSize: 18,
                          marginRight: 8,
                          opacity: s.padrao ? 0.4 : 1,
                        }}
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        title={
                          s.padrao
                            ? "Status padrão não pode ser excluído"
                            : "Apagar status"
                        }
                        onClick={() => !s.padrao && handleExcluir(s.id)}
                        style={{
                          border: "none",
                          
                          cursor: s.padrao ? "not-allowed" : "pointer",
                          fontSize: 18,
                          opacity: s.padrao ? 0.4 : 1,
                        }}
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default StatusPage;
